import React from "react";
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    BarController,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTranslation } from "react-i18next";
import {  pluginBg, toFormatPrice } from "../../../utils/utils";
import { SALDO_GRAPH_ID } from "../common";


Chart.register(
    ChartDataLabels,
    CategoryScale,
    BarController,
    LinearScale,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const GraphSaldo = ({ data }) => {

    const { t } = useTranslation()
    let maxLeft = 0;
    let maxWidth = 0;
    function generateLabelsNew(chart) {
        const datasets = chart.data.datasets;
        const { labels: { usePointStyle, pointStyle, textAlign, color } } = chart.legend.options;

        return chart._getSortedDatasetMetas().map((meta) => {
            const style = meta.controller.getStyle(usePointStyle ? 0 : undefined);
            const borderWidth = style.borderWidth;

            const box = meta?.controller?.chart?.legend?.legendHitBoxes[meta.index];

            if (box?.left > maxLeft) {
                maxLeft = box.left;
            }
            if (box?.width > maxWidth) {
                maxWidth = box.width;
            }
            const gradient = chart.ctx.createLinearGradient(maxLeft, 0, maxLeft + maxWidth, 0);
            // const gradient = chart.ctx.createLinearGradient(maxLeft, 0, maxLeft + maxWidth, 0);
            gradient.addColorStop(0, '#FCA5A599');
            gradient.addColorStop(.07, '#FCA5A599');
            gradient.addColorStop(.07, '#92DDA499');
            gradient.addColorStop(1, '#92DDA499');
            return {
                text: datasets[meta.index].label,
                fillStyle: datasets[meta.index].backgroundColor ? style.backgroundColor : gradient,
                fontColor: color,
                hidden: !meta.visible,
                lineCap: style.borderCapStyle,
                lineDash: style.borderDash,
                lineDashOffset: style.borderDashOffset,
                lineJoin: style.borderJoinStyle,
                lineWidth: (borderWidth.width + borderWidth.height) / 4,
                strokeStyle: style.borderColor,
                pointStyle: pointStyle || style.pointStyle,
                rotation: style.rotation,
                textAlign: textAlign || style.textAlign,
                borderRadius: 6,
                datasetIndex: meta.index
            };
        }, this);
    }
    const options = {
        stepped: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                align: 'start',
                labels: {
                    generateLabels: generateLabelsNew,
                    boxWidth: 12,
                    boxHeight: 12,
                    pointStyle: 'circle',
                    useBorderRadius: true,
                    borderRadius: 6,
                }
            },
            interaction: {
                intersect: false,
                mode: 'index',
            },
            tooltip: {
                position: 'nearest',
                mode: 'index',

                intersect: false,
                mode: 'index',

                backgroundColor: '#fff',
                bodyColor: '#444248',
                titleColor: '#444248',
                titleSpacing: 8,
                caretSize: 0,
                cornerRadius: 12,
                borderColor: '#EDEDED',
                borderWidth: 2,
                padding: 16,
                textDirection: 'top',
                titleFont: {
                    size: 18
                },
                bodyFont: {
                    size: 18
                },
                callbacks: {
                    title: function (tooltipItems, context) {
                        const label = tooltipItems[0].label
                        return label
                    },
                    label: function (context) {
                        if (!!context.dataset.fill) {
                            let label = context.dataset.label
                            label += ': ';
                            label += toFormatPrice(context.parsed.y, 2);
                        }
                        else {
                            let label = context.parsed.y !== 0 ? context.dataset.label : '';
                            if (context.parsed.y !== 0) {
                                if (label) {
                                    label += ': ';
                                }
                                label += toFormatPrice(context.parsed.y, 2);
                            }

                            return label;
                        }

                    },
                    labelColor: function (context) {
                        if (!!context.dataset.fill) {
                            const balanceVal = context?.dataset?.data?.[context?.dataIndex] === 0 ? '#eee' : context?.dataset?.data?.[context?.dataIndex] > 0 ? context.dataset.fill.above : context.dataset.fill.below
                            return {
                                backgroundColor: balanceVal,
                                border: 0,
                                boxWidth: 10,
                                boxHeight: 10,
                                pointStyle: 'circle',
                                useBorderRadius: true,
                                borderRadius: 8,
                            }
                        }
                        else {
                            return {
                                backgroundColor: context.dataset.backgroundColor,
                                border: 0,
                                boxWidth: 10,
                                boxHeight: 10,
                                pointStyle: 'circle',
                                useBorderRadius: true,
                                borderRadius: 8,
                            }
                        }
                    }
                }
            },
            datalabels: {
                display: false,
            }
        },
        responsive: true,
        scales: {
            x: {
                border: {
                    display: false,
                },
                grid: {
                    drawBorder: false,
                    display: false,
                },
                stacked: true,
                ticks: {
                    maxRotation: 0,
                }
            },
            y: {
                display: false,
                offset: true,
                stacked: true,
                grid: {
                    drawBorder: false,
                    display: false,
                },
            },
        },
    };


    const datachar = {
        labels: data?.labels,
        datasets: [
            {
                animation: false,
                transitions: false,
                label: t('analytics.sum_invoice'),
                data: data.sum_invoice,
                backgroundColor: '#FCA5A599',
                borderRadius: 4,
                spanGaps: false,
                datalabels: {
                    align: 'start',
                    anchor: 'start',
                    formatter: function (value, context) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        // const isAbs = context?.dataset?.data?.[context?.dataIndex] > 0
                        return '#EF4444';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }

            },
            {
                animation: false,
                transitions: false,
                label: t('analytics.sum_payment'),
                data: data.sum_payment,
                backgroundColor: '#92DDA499',
                borderRadius: 4,
                spanGaps: true,
                datalabels: {
                    // display: true,
                    align: 'end',
                    // rotation: '-90',
                    anchor: 'end',
                    formatter: function (value, context) {
                        return `${toFormatPrice(value)}`;
                    },
                    color: function (context) {
                        if (!context?.dataset?.data?.[context?.dataIndex]) return '#00000000'
                        // const isAbs = context?.dataset?.data?.[context?.dataIndex] > 0
                        // return isAbs ? '#139A32' : '#EF4444';
                        return '#139A32';
                    },
                    font: {
                        weight: 500,
                        size: 10,
                        family: 'Inter'
                    }
                }
            },
            {
                animation: false,
                transitions: false,
                label: t('analytics.balance'),
                type: "line",
                data: data.balance,
                pointStyle: false,
                spanGaps: true,
                fill: { above: '#92dda442', below: '#fca5a55c', target: { value: 0 } },
                borderColor: "rgba(255, 254, 254, 0)",

            },
        ],
    }
    return (
        <Bar
            id={SALDO_GRAPH_ID}
            options={options}
            data={datachar}
            plugins={[pluginBg]}
        />
    )
}

export default GraphSaldo